/* global previews */
import { fabric } from "fabric";
import { CUSTOM_TEXT_BOX_CLASS_NAME, CustomTextBox } from './nodes/custom-text-box';
import { initOvalShadowBehind, initOvalShadowInFront} from "@frontend/oval-shadow";
import {
  ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID,
  BACKGROUND_THEME_IMAGE_PREFIX_ID,
  IMAGE_PREFIX_ID
} from "@frontend/constants/templates-field-prefixes";
import {BEHIND_DISPLAY_MODE, IN_FRONT_DISPLAY_MODE} from "@frontend/constants/oval-shadows";
import {createCanvasResizeControl} from "@frontend/canvas-resize/create-canvas-resize-controler";
import {Effects} from "@frontend/lib/fabric/effects/Effects";
import {fxCanvas} from "@frontend/lib/glfx";

// This function breaks the text editor. Text editor can not edit text for the second time.
// rotateControl()

fabric.Object.prototype.locked = false
Object.defineProperty(fabric.Object.prototype, 'locked', {
  get: function () {
    return this._locked || false
  },
  set: function (value) {
    this._locked = value
    this.lockMovementX = value
    this.lockMovementY = value
    this.lockScalingX = value
    this.lockScalingY = value
    this.lockRotation = value
    this.lockUniScaling = value
    this.editable = !value
  },
})

fabric.Object.prototype.drawCacheOnCanvas = function(ctx) {
  ctx.scale(1 / this.zoomX, 1 / this.zoomY);
  if (this._cacheCanvas.width > 0 && this._cacheCanvas.height > 0) {
    ctx.drawImage(this._cacheCanvas, -this.cacheTranslationX, -this.cacheTranslationY);
  }
}

fabric.Text.prototype.getLineSpacing = function (lineIndex) {
  let lineSpacing = this.lineHeight;
  const prevLineIndex = lineIndex - 1;

  if (prevLineIndex >= 0) {
    lineSpacing = this.getValueOfPropertyAt(prevLineIndex, 0, 'lineSpacing') ?? this.lineHeight;
  }
  return lineSpacing
}


function getJustifyInterCharSpacing(_this, lineIndex, overflowSpecialChars) {
  const line = _this._textLines[lineIndex];
  let totalTextWidth = 0;
  let overflowedCharWidth = 0;

  const overflowableChars = [`,`, `.`, `"`, `'`, '`'];
  for (var i = 0; i < line.length; i++) {
    if (overflowSpecialChars && i === (line.length - 1) && overflowableChars.includes(line[i])) {
      overflowedCharWidth = _this.__charBounds[lineIndex][i].width;
    }
    totalTextWidth += _this.__charBounds[lineIndex][i].width;
  }

  let totalWidth = _this.width - totalTextWidth + overflowedCharWidth;
  return totalWidth / (line.length - 1);
}

fabric.IText.prototype.renderCursorOrSelection = function() {
  if (!this.isEditing || !this.canvas || !this.canvas.contextTop) {
    return;
  }
  var boundaries = this._getCursorBoundaries(),
      ctx = this.canvas.contextTop;
  this.clearContextTop(true);
  if (this.selectionStart === this.selectionEnd) {
    this.renderCursor(boundaries, ctx);
  }
  else {
    this.renderSelection(boundaries, ctx);
  }
  ctx.restore();
}

fabric.IText.prototype._getCursorBoundariesOffsets = function(position) {
  if (this.cursorOffsetCache && 'top' in this.cursorOffsetCache) {
    return this.cursorOffsetCache;
  }
  var lineLeftOffset,
      lineIndex,
      charIndex,
      topOffset = 0,
      leftOffset = 0,
      boundaries,
      cursorPosition = this.get2DCursorLocation(position);
  charIndex = cursorPosition.charIndex;
  lineIndex = cursorPosition.lineIndex;
  for (var i = 0; i < lineIndex; i++) {
    topOffset += this.getHeightOfLine(i);
  }
  lineLeftOffset = this._getLineLeftOffset(lineIndex);
  var bound = this.__charBounds[lineIndex][charIndex];
  if (bound) {
    if (this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1) {
      const overflowSpecialChars = this.textAlign.indexOf('justify-inter-char-overflow') !== -1
      const charSpacing = getJustifyInterCharSpacing(this, lineIndex, overflowSpecialChars);
      leftOffset = bound.left + ((charIndex - 1) * charSpacing);
    } else {
      leftOffset = bound.left;
    }
  }
  if (this.charSpacing !== 0 && charIndex === this._textLines[lineIndex].length) {
    leftOffset -= this._getWidthOfCharSpacing();
  }
  boundaries = {
    top: topOffset,
    left: lineLeftOffset + (leftOffset > 0 ? leftOffset : 0),
  };
  this.cursorOffsetCache = boundaries;
  return this.cursorOffsetCache;
}


fabric.Text.prototype._render = function(ctx) {
  const isJustifyInterChar = this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1;
  if (isJustifyInterChar) {
    this.charSpacing = 0;
  }

  const path = this.path;
  path && !path.isNotVisible() && path._render(ctx);
  this._setTextStyles(ctx);
  this._renderTextLinesBackground(ctx);
  this._renderTextDecoration(ctx, 'underline');
  this._renderText(ctx);
  this._renderTextDecoration(ctx, 'overline');
  this._renderTextDecoration(ctx, 'linethrough');
}

fabric.Text.prototype._renderChars = function(method, ctx, line, left, top, lineIndex) {
  // set proper line offset
  var lineHeight = this.getHeightOfLine(lineIndex),
      isJustifyInterWord = this.textAlign.indexOf('justify') !== -1,
      isJustifyInterChar = this.textAlign.indexOf('justify-inter-char') !== -1 || this.textAlign.indexOf('justify-inter-char-overflow') !== -1,
      overflowSpecialChars = this.textAlign.indexOf('justify-inter-char-overflow') !== -1,
      actualStyle,
      nextStyle,
      charsToRender = '',
      charBox,
      boxWidth = 0,
      timeToRender,
      shortCut = !isJustifyInterWord && !isJustifyInterChar && this.charSpacing === 0 && this.isEmptyStyles(lineIndex),
      charSpacing = this.charSpacing;

  ctx.save();
  top -= lineHeight * this._fontSizeFraction / this.getLineSpacing(lineIndex);

  if (shortCut) {
    // render all the line in one pass without checking
    this._renderChar(method, ctx, lineIndex, 0, line.join(''), left, top, lineHeight);
    ctx.restore();
    return;
  }

  if (isJustifyInterChar) {
    charSpacing = getJustifyInterCharSpacing(this, lineIndex, overflowSpecialChars);
  }

  for (var i = 0, len = line.length - 1; i <= len; i++) {
    timeToRender = i === len || charSpacing;
    charsToRender += line[i];
    charBox = this.__charBounds[lineIndex][i];

    if (boxWidth === 0) {
      left += charBox.kernedWidth - charBox.width;
      boxWidth += charBox.width;
    } else {
      boxWidth += charBox.kernedWidth;
    }

    if (isJustifyInterWord && !timeToRender) {
      if (this._reSpaceAndTab.test(line[i])) {
        timeToRender = true;
      }
    }

    if (!timeToRender) {
      // if we have charSpacing, we render char by char
      actualStyle = actualStyle || this.getCompleteStyleDeclaration(lineIndex, i);
      nextStyle = this.getCompleteStyleDeclaration(lineIndex, i + 1);
      timeToRender = this._hasStyleChanged(actualStyle, nextStyle);
    }

    if (timeToRender) {
      this._renderChar(method, ctx, lineIndex, i, charsToRender, left, top, lineHeight);
      charsToRender = '';
      actualStyle = nextStyle;
      left += isJustifyInterChar ? boxWidth + charSpacing : boxWidth;
      boxWidth = 0;
    }
  }

  ctx.restore();
}

fabric[CUSTOM_TEXT_BOX_CLASS_NAME] = CustomTextBox;

export const EXTRA_FEATURES = {
  TRANSFORM_IN_GROUP: 'EXTRA_TRANSFORM_IN_GROUP'
}

export const recalculateGroupPoint = (width, height, point, isToCanvas = true) => {
  const hw = width / 2
  const hh = height / 2
  return {
    x: point.x + hw * (isToCanvas ? 1 : -1),
    y: point.y + hh * (isToCanvas ? 1 : -1)
  }
}

fabric.Canvas.prototype._transformObject = function(e) {
  var pointer = this.getPointer(e),
      transform = this._currentTransform;

  transform.reset = false;
  transform.shiftKey = e.shiftKey;
  transform.altKey = e[this.centeredKey];

  const target = this.getActiveObject();

  if (target.group && (target.extraFeatures).includes(EXTRA_FEATURES.TRANSFORM_IN_GROUP) && !['drag'].includes(transform.action)) {
    const groupPoint = recalculateGroupPoint(target.group.width, target.group.height, pointer, false)
    pointer.x = groupPoint.x
    pointer.y = groupPoint.y

    target.group.dirty = true
  }

  this._performTransformAction(e, transform, pointer);
  transform.actionPerformed && this.requestRenderAll();
}

const propProcessor = (obj, callback) => {
  return Object.keys(obj)
      .map(key => ({ key, value: callback(obj[key]) }))
      .reduce((acc, { key, value }) => {
        acc[key] = value
        return acc
      }, {})}

const coordsRecalc = (_oCoords, width, height) => propProcessor(_oCoords, (point) => ({
  ...recalculateGroupPoint(width, height, point),
  corner: point.corner ? propProcessor(point.corner, (cPoint) => recalculateGroupPoint(width, height, cPoint)) : undefined,
  touchCorner: point.touchCorner ? propProcessor(point.touchCorner, (cPoint) => recalculateGroupPoint(width, height, cPoint)) : undefined,
}))

fabric.Object.prototype._findTargetCorner = function (...args) {
  if (this.extraFeatures.includes(EXTRA_FEATURES.TRANSFORM_IN_GROUP)) {
    return this._findTargetCornerWithGroup(...args)
  }

  return this._findTargetCornerOriginal(...args)
}

fabric.Object.prototype._findTargetCornerOriginal = function(pointer, forTouch) {
  // objects in group, anykind, are not self modificable,
  // must not return an hovered corner.
  if (!this.hasControls || this.group || (!this.canvas || this.canvas._activeObject !== this)) {
    return false;
  }

  var ex = pointer.x,
      ey = pointer.y,
      xPoints,
      lines, keys = Object.keys(this.oCoords),
      j = keys.length - 1, i;
  this.__corner = 0;

  // cycle in reverse order so we pick first the one on top
  for (; j >= 0; j--) {
    i = keys[j];
    if (!this.isControlVisible(i)) {
      continue;
    }

    lines = this._getImageLines(forTouch ? this.oCoords[i].touchCorner : this.oCoords[i].corner);
    // // debugging
    //
    // this.canvas.contextTop.fillRect(lines.bottomline.d.x, lines.bottomline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.bottomline.o.x, lines.bottomline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.leftline.d.x, lines.leftline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.leftline.o.x, lines.leftline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.topline.d.x, lines.topline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.topline.o.x, lines.topline.o.y, 2, 2);
    //
    // this.canvas.contextTop.fillRect(lines.rightline.d.x, lines.rightline.d.y, 2, 2);
    // this.canvas.contextTop.fillRect(lines.rightline.o.x, lines.rightline.o.y, 2, 2);

    xPoints = this._findCrossPoints({ x: ex, y: ey }, lines);
    if (xPoints !== 0 && xPoints % 2 === 1) {
      this.__corner = i;
      return i;
    }
  }
  return false;
}

fabric.Object.prototype._findTargetCornerWithGroup = function(pointer, forTouch) {
  // objects anykind, are not self modificable,
  // must not return an hovered corner.
  if (!this.hasControls || (!this.canvas || this.canvas._activeObject !== this)) {
    return false;
  }

  const oCoords = coordsRecalc(this.oCoords, this.group.width, this.group.height)

  var ex = pointer.x,
      ey = pointer.y,
      xPoints,
      lines, keys = Object.keys(oCoords),
      j = keys.length - 1, i;
  this.__corner = 0;

  // cycle in reverse order so we pick first the one on top
  for (; j >= 0; j--) {
    i = keys[j];
    if (!this.isControlVisible(i)) {
      continue;
    }

    lines = this._getImageLines(forTouch ? oCoords[i].touchCorner : oCoords[i].corner);

    xPoints = this._findCrossPoints({ x: ex, y: ey }, lines);
    if (xPoints !== 0 && xPoints % 2 === 1) {
      this.__corner = i;
      return i;
    }
  }
  return false;
}
fabric.Object.prototype.extraFeatures = [];
fabric.Image.prototype.ovalShadow = null;
fabric.Image.prototype.updateOvalShadowData = function () {
  if (!Object.hasOwn(this, 'updateLayoutChanges')) return;
  const preview = previews.find(preview => preview.id === this.canvas.instance_id );
  const key = 'oval_shadows';
  const OVAL_SHADOW_GROUP_ID_KEY = 'p_oval_shadow_group_ids';
  const BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY = 'bk_img_oval_shadow_group_ids';
  const ADD_BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY = 'add_bk_img_oval_shadow_group_ids';
  
  if (this.id.startsWith(IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadowsByProduct = preview.data['p_'+ key] || [];
    shadowsByProduct[parseInt(index)] = this.ovalShadow;
    
    const productImages = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    
    this.updateLayoutChanges(this.canvas.instance_id, 'p_'+ key, shadowsByProduct);
    this.updateLayoutChanges(this.canvas.instance_id, OVAL_SHADOW_GROUP_ID_KEY, productImages.map(el => el.ovalShadowGroupId));
  }
  
  if (this.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.ovalShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));

    this.updateLayoutChanges(this.canvas.instance_id, 'bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY, images.map(el => el.ovalShadowGroupId));
  }
  
  if (this.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID)) {
    const index = this.id.split('_').pop();
    const shadows = preview.data['add_bk_img_'+ key] || [];
    shadows[parseInt(index)] = this.ovalShadow;

    const images = preview.canvas.getObjects()
      .filter(el => el.id.startsWith(ADDITIONAL_BACKGROUND_THEME_IMAGE_PREFIX_ID))
      .sort((a, b) => a.id.localeCompare(b.id));
    this.updateLayoutChanges(this.canvas.instance_id, 'add_bk_img_'+ key, shadows);
    this.updateLayoutChanges(this.canvas.instance_id, ADD_BACKGROUND_THEME_IMAGE_OVAL_SHADOW_GROUP_ID_KEY, images.map(el => el.ovalShadowGroupId));
  }
}
fabric.Image.prototype._render = (function(_render) {
  return function(ctx) {
    if (this.getSrc() && this.ovalShadow && this.ovalShadow.find(shadow => shadow.displayMode === BEHIND_DISPLAY_MODE)) {
      initOvalShadowBehind(this.ovalShadow.filter(shadow => shadow.displayMode === BEHIND_DISPLAY_MODE), this, ctx)
    }
    _render.call(this, ctx);
    if (this.getSrc() && this.ovalShadow && this.ovalShadow.find(shadow => shadow.displayMode === IN_FRONT_DISPLAY_MODE)) {
      initOvalShadowInFront(this.ovalShadow.filter(shadow => shadow.displayMode === IN_FRONT_DISPLAY_MODE), this, ctx)
    }
  };
})(fabric.Image.prototype._render);

fabric.Canvas.prototype.initialize = (function (originalFn) {
  return function (...args) {
    originalFn.call(this, ...args);

    const options = args[1];

    if (options?.resizeControl) {
      createCanvasResizeControl(this);
    }
  }
})(fabric.Canvas.prototype.initialize);

fabric.Canvas.prototype._checkTarget = function(pointer, obj, globalPointer) {
  if (
    obj &&
    obj.visible &&
    obj.evented &&
    obj.containsPoint(pointer)
  ) {
    if ((this.perPixelTargetFind || obj.perPixelTargetFind) && !obj.isEditing) {
      return this.isTargetTransparent(obj, globalPointer.x, globalPointer.y)
    }

    return obj.selectableBordersOnly ? !this.isInsideBorder(obj, pointer) : true;
  }
}

fabric.Canvas.prototype.isInsideBorder = function (target, pointer) {
  if (!target.strokeWidth) {
    return;
  }

  const pointerCoords = target.getLocalPointer(pointer);

  const strokeMargin = target.strokeWidth;
  const scaledWidth = target.getScaledWidth();
  const scaledHeight = target.getScaledHeight();

  return (
    pointerCoords.x > strokeMargin &&
    pointerCoords.x < scaledWidth - strokeMargin &&
    pointerCoords.y > strokeMargin &&
    pointerCoords.y < scaledHeight - strokeMargin
  );
}

const originalRender = fabric.Textbox.prototype._render;
const originalInitialize = fabric.Textbox.prototype.initialize;
// const originalGetFontDeclaration = fabric.Textbox.prototype._getFontDeclaration;
const originalSet = fabric.Textbox.prototype._set;
const originalObjectRender = fabric.Object.prototype.render;

fabric.Textbox.prototype._set = function(key, value) {
  if (!this.isCurved) {
    originalSet.call(this, key, value);

    return;
  }

  switch(key) {
    case 'scaleX':
      this.fontSize *= value;
      this.diameter *= value;
      this.width *= value;
      this.scaleX = 1;
      if (this.width < 1) { this.width = 1; }
      break;

    case 'scaleY':
      this.height *= value;
      this.scaleY = 1;
      if (this.height < 1) { this.height = 1; }
      break;

    default:
      this.callSuper('_set', key, value);
      break;
  }

}

// fabric.Textbox.prototype._getFontDeclaration = function() {
//   if (!this.isCurved) {
//     return originalGetFontDeclaration.call(this);
//   }
//  
//   return [
//     // node-canvas needs "weight style", while browsers need "style weight"
//     (fabric.isLikelyNode ? this.fontWeight : this.fontStyle),
//     (fabric.isLikelyNode ? this.fontStyle : this.fontWeight),
//     this.fontSize + 'px',
//     (fabric.isLikelyNode ? ('"' + this.fontFamily + '"') : this.fontFamily)
//   ].join(' ');
//
// }

// The positioning of text is wrong on the first render after applying curved for multiline texts. This solution is not perfect, but it resolves the issue.
const doubleRender = []

const removeItemFromDoubleRender = (item) => {
  if (item.element_id) {
    const index = doubleRender.indexOf(item.element_id)
    if (index > -1) {
      doubleRender.splice(index, 1)
    }
  }
}
fabric.Textbox.prototype.generateResolvedStyleArray = function (preparedText) {
  const text = preparedText || this.text
  const lines = text.split('\n');
  const resolved = [];
  const styles = text ? this.styles || {} : {};
  let lastStyle = {
    fontSize: this.fontSize,
    fill: this.fontColor || this.fill,
    fontFamily: this.fontFamily,
  };
  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex];
    const styleLine = [];
    const lineStyles = styles[lineIndex] || {};

    for (let charIndex = 0; charIndex < line.length; charIndex++) {
      let currentStyle = lineStyles[charIndex];

      if (!currentStyle) {
        currentStyle = { ...lastStyle };
      } else {
        currentStyle = { ...currentStyle };
      }

      styleLine[charIndex] = currentStyle;
      lastStyle = currentStyle;
    }

    resolved[lineIndex] = styleLine;
  }
  return resolved;
};
fabric.Textbox.prototype.getCircularText = function () {
  let text = this.text;

  const styles = Object.keys(this.styles).reduce((acc, rowKey, index) => {
    const row = this.styles[rowKey];
    const items = Object.keys(row).map(itemKey => row[itemKey]);
    if (index !== Object.keys(this.styles).length - 1) {
      items.push(items[items.length - 1]);
    }
    return acc.concat(items);
  }, []).reverse();

  let diameter = this.curvedDiameter || 0;

  const fontSize = Number(this.fontSize);
  const kerning = (parseFloat(this.charSpacing || 0) / 1000) * fontSize; // <-- charSpacing in px

  const flipped = this.flipped || false;
  const fill = this.fill;
  let inwardFacing = true;
  let startAngle = 0;
  const canvas = fabric.util.createCanvasElement();
  const ctx = canvas.getContext('2d');
  let cw;
  let clockwise = -1;

  if (flipped) {
    startAngle = 180;
    inwardFacing = false;
  }

  startAngle *= Math.PI / 180;
  const textHeight = fontSize;

  canvas.width = canvas.height = diameter;
  ctx.font = this._getFontDeclaration();

  if (inwardFacing) {
    text = text.split('').reverse().join('');
  }

  ctx.translate(diameter / 2, diameter / 2);
  startAngle += (Math.PI * !inwardFacing);
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';

  for (let x = 0; x < text.length; x++) {
    cw = ctx.measureText(text[x]).width;
    startAngle += ((cw + (x === text.length - 1 ? 0 : kerning)) / (diameter / 2 - textHeight)) / 2 * -clockwise;
  }

  ctx.rotate(startAngle);
  for (let x = 0; x < text.length; x++) {
    cw = ctx.measureText(text[x]).width;
    ctx.rotate((cw / 2) / (diameter / 2 - textHeight) * clockwise);
    const y = (inwardFacing ? 1 : -1) * (0 - diameter / 2 + textHeight / 2);
    if (this.strokeStyle && this.strokeWidth) {
      ctx.strokeStyle = this.strokeStyle;
      ctx.lineWidth = this.strokeWidth;
      ctx.miterLimit = 2;
      ctx.strokeText(text[x], 0, y);
    }
    ctx.fillStyle = styles[x]?.fill || fill;
    ctx.fillText(text[x], 0, y);

    ctx.rotate((cw / 2 + kerning) / (diameter / 2 - textHeight) * clockwise);
  }

  removeItemFromDoubleRender(this);
  return canvas;
};


fabric.Textbox.prototype._trimCanvas = function(canvas) {
  const pix = {x:[], y:[]};
  const ctx = canvas.getContext('2d');
  let w = canvas.width;
  let h = canvas.height;
  let n;
  const imageData = ctx.getImageData(0, 0, w, h);

  const fn = function(a,b) { return a - b };

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (imageData.data[((y * w + x) * 4)+3] > 0) {
        pix.x.push(x);
        pix.y.push(y);
      }
    }
  }

  pix.x.sort(fn);
  pix.y.sort(fn);
  n = pix.x.length-1;

  w = pix.x[n] - pix.x[0];
  h = pix.y[n] - pix.y[0];
  const cut = ctx.getImageData(pix.x[0], pix.y[0], w, h);

  canvas.width = w;
  canvas.height = h;
  ctx.putImageData(cut, 0, 0);
}

fabric.Textbox.prototype.initialize = function (text, options) {
  if (!options.isCurved) {
    originalInitialize.call(this, text, options);

    return;
  }

  options || (options = {});
  this.text = text;

  this.callSuper('initialize', text, options);
  this.set('lockUniScaling', true);

  // Draw curved text here initially too, while we need to know the width and height.
  const canvas = this.getCircularText();
  this._trimCanvas(canvas);
  this.set('height', canvas.height);
}

fabric.Textbox.prototype._render = function (ctx) {
  if (!this.isCurved) {
    removeItemFromDoubleRender(this)
    originalRender.call(this, ctx);
    
    return;
  }
  
  const canvas = this.getCircularText();
  this._trimCanvas(canvas);

  let offsetX = 0

  switch (this.textAlign) {
    case 'right':
      offsetX = this.width - canvas.width;
      break;
    case 'left':
      offsetX = (this.width - canvas.width) * -1
  }

  this.set('height', canvas.height);

  ctx.drawImage(canvas, (offsetX + -canvas.width) / 2, -canvas.height / 2, canvas.width, canvas.height);

  this.setCoords();
  if (this.element_id && !doubleRender.includes(this.element_id)) {
    this.canvas.requestRenderAll()
    doubleRender.push(this.element_id)
  }
}

fabric.Object.prototype.render = function (ctx, ...props) {
  const effectsApplied = Effects.applyEffects(this, ctx, ...props);

  if (!effectsApplied) {
    return originalObjectRender.call(this, ctx, ...props);
  }
}

const cornerNames = ['tl', 'tr', 'br', 'bl'];

const getSkewIndex = {
  tl: [0, 1],
  tr: [2, 3],
  br: [4, 5],
  bl: [6, 7],
};

cornerNames.forEach(corner => {
  fabric.Object.prototype.controls[corner + '_skew'] = new fabric.Control({
    actionName: 'skew',
    sizeX: 40,
    sizeY: 40,
    getVisibility: function(fabricObject, controlKey) {
      const objectVisibility = fabricObject._controlsVisibility;
      if (objectVisibility && typeof objectVisibility[controlKey] !== 'undefined') {
        return objectVisibility[controlKey];
      }
      const skew = fabricObject.smartObject?.skew || fabricObject.skew;
      return skew && skew.render && !fabricObject.smartObject;
    },
    positionHandler: function (dim, finalMatrix, fabricObject) {
      const coords = fabricObject.wCoords || fabricObject.oCoords;

      if (!coords) {
        return {x: 0, y: 0}
      }

      const cornerOffsets = {
        tl: [coords.tl.x, coords.tl.y],
        tr: [coords.tr.x, coords.tr.y],
        br: [coords.br.x, coords.br.y],
        bl: [coords.bl.x, coords.bl.y],
      };

      const [dx, dy] = cornerOffsets[corner];

      return {
        x: dx,
        y: dy
      };
    },
    actionHandler: function (eventData, transform, x, y) {
      const fabricObject = transform.target;
      const idx = getSkewIndex[corner];
      const last = transform.lastX !== undefined ? [transform.lastX, transform.lastY] : [x, y];
      const dx = x - last[0];
      const dy = y - last[1];
      transform.lastX = x;
      transform.lastY = y;

      fabricObject.skew.value[idx[0]] += dx;
      fabricObject.skew.value[idx[1]] += dy;

      fabricObject.fire('skew:changed', {
        corner,
        dx,
        dy,
        skew: fabricObject.skew?.value,
        pointer: { x, y }
      });

      fabricObject.canvas.requestRenderAll();
      return true;
    },
    render: function (ctx, left, top, styleOverride, fabricObject) {
      const zoom = fabricObject.canvas.getZoom();

      ctx.save();
      ctx.fillStyle = 'red';
      ctx.beginPath();
      ctx.arc(left, top, 20 / zoom, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      const coords = fabricObject.wCoords || fabricObject.oCoords;
      const center = {
        x: (coords.tl.x + coords.tr.x + coords.br.x + coords.bl.x) / 4,
        y: (coords.tl.y + coords.tr.y + coords.br.y + coords.bl.y) / 4
      };

      ctx.save();
      ctx.strokeStyle = 'rgba(0,0,0,0.5)';
      ctx.lineWidth = 1 / zoom;
      ctx.beginPath();
      ctx.moveTo(center.x, center.y);
      ctx.lineTo(left, top);
      ctx.stroke();
      ctx.restore();
    },
    cursorStyleHandler: function () {
      return 'pointer';
    },
  });
});

function flattenText(text) {
  const lines = text.split('\n');
  const result = [];
  lines.forEach((lineText, lineIndex) => {
    [...lineText].forEach((char, i) => {
      result.push({ char, line: lineIndex, indexInLine: i });
    });
  });
  return result;
}

function getStyleMapping(oldFlat, newFlat) {
  const m = oldFlat.length;
  const n = newFlat.length;
  const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      if (oldFlat[i].char === newFlat[j].char) {
        dp[i][j] = 1 + dp[i + 1][j + 1];
      } else {
        dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1]);
      }
    }
  }

  const mapping = {};
  let i = 0, j = 0;

  while (i < m && j < n) {
    if (oldFlat[i].char === newFlat[j].char) {
      mapping[j] = i;
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      i++;
    } else {
      j++;
    }
  }

  return mapping;
}

function transferCharStyles(oldText, newText, oldStyles) {
  const oldFlat = flattenText(oldText);
  const newFlat = flattenText(newText);
  const mapping = getStyleMapping(oldFlat, newFlat);

  const newStyles = {};

  newFlat.forEach(({ char, line, indexInLine }, flatIndex) => {
    const mappedOldIndex = mapping[flatIndex];
    if (mappedOldIndex !== undefined) {
      const { line: oldLine, indexInLine: oldIndex } = oldFlat[mappedOldIndex];
      const style = oldStyles?.[oldLine]?.[oldIndex];
      if (style) {
        if (!newStyles[line]) newStyles[line] = {};
        newStyles[line][indexInLine] = { ...style };
      }
    }
  });

  return newStyles;
}


fabric.IText.prototype.transferStylesFrom = function(oldText, oldStyles) {
  const newText = this.text;
  this.styles = transferCharStyles(oldText, newText, oldStyles);
};

fabric.IText.prototype.updateStylesFrom = function (newStyles) {
  const oldStyles = this.styles || {};
  const cleaned = {};

  // Step 1: Remove all old styles that are not present in newStyles
  for (const line in newStyles) {
    if (oldStyles[line]) {
      for (const char in newStyles[line]) {
        if (oldStyles[line] && oldStyles[line][char]) {
          if (!cleaned[line]) cleaned[line] = {};
          cleaned[line][char] = oldStyles[line][char];
        }
      }
    }
  }

  // Step 2: Add new styles that do not already exist
  for (const line in newStyles) {
    if (!cleaned[line]) cleaned[line] = {};
    for (const char in newStyles[line]) {
      if (!(char in cleaned[line])) {
        cleaned[line][char] = newStyles[line][char];
      }
    }
  }

  // Step 3: Apply the updated styles to the text object
  this.styles = cleaned;
  this.dirty = true;
  this.setCoords();
  this.canvas?.requestRenderAll();
};

